import * as React from 'react'
import { useState, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import clx from 'classnames'

import { CheckIcon } from '../icons/Check'
import { faChevronDownSolid, FaIcon } from '@/lib/fa'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { SidebarTooltip } from '@/modules/shared/components/SidebarTooltip'

export type Option = {
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type Props = {
  placeholder?: string
  tooltip?: string
  icon?: React.ElementType
  options: Array<Option>
  className?: string
  value?: string | null
  onChange?: (value: any) => void
  name?: string
  disabled?: boolean
  hideArrow?: boolean
  collapsed?: boolean
  searchPlaceholder?: string
}

export const DropdownSideBar = ({
  placeholder = 'Wybierz...',
  tooltip,
  icon: Icon = null,
  options,
  className,
  value,
  disabled,
  hideArrow = false,
  name,
  collapsed,
  onChange,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [query, setQuery] = useState('')
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const selected = options.find((o) => o.value === value) || null

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const portalRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        portalRef.current &&
        !portalRef.current.contains(target)
      ) {
        setOpen(false)
        setQuery('')
        setFocusedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = q
      ? options.filter(
          (o) =>
            o.label.toString().toLowerCase().includes(q) ||
            o.value.toString().toLowerCase().includes(q),
        )
      : options

    return [...base].sort((a, b) => {
      if (selected?.value === a.value) return -1
      if (selected?.value === b.value) return 1
      return a.label
        .toString()
        .localeCompare(b.label.toString(), 'pl', { sensitivity: 'base' })
    })
  }, [options, query, selected?.value])

  const toggle = () => {
    if (!disabled) {
      if (!open && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        // The menu portals to <body>, so it competes directly with the
        // payment-required modal overlay (z-index 99999). While that modal is
        // open the sidebar is lifted above the overlay (see the
        // `body.payment-required-open` rule in tailwind.css) so the user can
        // still switch company — lift this portaled menu above the overlay too,
        // otherwise it opens behind it and can't be clicked.
        const paymentModalOpen = document.body.classList.contains(
          'payment-required-open',
        )
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          minWidth: Math.max(rect.width, 220),
          zIndex: paymentModalOpen ? 100001 : 9999,
        })
      }
      setOpen((v) => !v)
    }
  }

  const select = (option: Option) => {
    setOpen(false)
    setQuery('')
    setFocusedIndex(-1)
    onChange?.(option.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (
        e.key === 'Enter' ||
        e.key === ' ' ||
        (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key))
      ) {
        e.preventDefault()
        setOpen(true)
        if (e.key.length === 1) setQuery(e.key)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filteredOptions[focusedIndex]
      if (option) select(option)
    } else if (e.key === 'Escape') {
      setOpen(false)
      setQuery('')
      setFocusedIndex(-1)
    }
  }

  useEffect(() => {
    if (open) {
      setFocusedIndex(filteredOptions.length ? 0 : -1)
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }, [open, filteredOptions.length])

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as
        | HTMLElement
        | undefined
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  return (
    <div
      ref={dropdownRef}
      className={clx('tw-relative tw-w-full', className)}
      tabIndex={0}
      role="combobox"
      aria-expanded={open}
      onKeyDown={handleKeyDown}
    >
      <SidebarTooltip
        text={tooltip ?? placeholder}
        enabled={Boolean(collapsed)}
        className="tw-w-full"
      >
        <button
          ref={triggerRef}
          type="button"
          aria-label={collapsed ? (tooltip ?? placeholder) : undefined}
          onClick={toggle}
          className={clx(
            'tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-between tw-px-2 tw-py-3 tw-text-left tw-transition-all',
            !open && 'hover:tw-opacity-80',
          )}
        >
          <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 tw-font-medium">
            {Icon && (
              <span className="tw-flex-shrink-0 tw-p-0.5">
                <Icon
                  className={clx(
                    'tw-h-4 tw-w-4',
                    selected ? 'tw-text-content-base' : 'tw-text-neutral',
                  )}
                />
              </span>
            )}

            {!collapsed && (
              <span
                className={clx(
                  'tw-min-w-0 tw-flex-1 tw-truncate tw-whitespace-nowrap',
                  selected ? 'tw-text-content-base' : 'tw-text-neutral',
                )}
              >
                {selected ? selected.label : placeholder}
              </span>
            )}
          </span>

          {!hideArrow && !collapsed ? (
            <FaIcon
              icon={faChevronDownSolid}
              className={clx(
                'tw-hidden tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition-transform md:tw-flex',
                open && 'tw-rotate-180 tw-rounded-full tw-bg-secondary',
                selected ? 'tw-text-content-base' : 'tw-text-neutral',
              )}
            />
          ) : null}
        </button>
      </SidebarTooltip>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={portalRef}
            style={dropdownStyle}
            className="tw-rounded-md tw-bg-white tw-shadow-multi"
          >
            {!collapsed && options && options?.length > 5 && (
              <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-p-2">
                <div className="tw-relative">
                  <div className="">
                    <input
                      ref={searchRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Szukaj..."
                      className="tw-w-full tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-px-3 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-focus-100"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {query && (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery('')
                        searchRef.current?.focus()
                      }}
                      className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-rounded hover:tw-bg-base-300"
                    >
                      <FaIcon
                        icon={faClose}
                        className="tw-h-3 tw-w-3 tw-opacity-70"
                      />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="custom-scrollbar hide-scrollbar tw-max-h-[300px] tw-overflow-y-auto tw-p-1.5">
              {filteredOptions.length === 0 ? (
                <div className="tw-p-2 tw-text-sm tw-opacity-70">
                  Brak wyników
                </div>
              ) : (
                <ul ref={listRef} className="tw-flex tw-flex-col tw-gap-0.5">
                  {filteredOptions.map((option, idx) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        onClick={() => select(option)}
                        className={clx(
                          'tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-px-1 tw-py-2 hover:tw-bg-base-300',
                          selected?.value === option.value &&
                            'tw-bg-secondary tw-font-medium',
                          focusedIndex === idx && 'tw-bg-base-300',
                        )}
                      >
                        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-start tw-gap-1">
                          {option.icon && (
                            <option.icon className="tw-h-3 tw-flex-shrink-0" />
                          )}
                          <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-medium tw-text-content-base">
                            {option.content || option.label}
                          </span>
                        </span>

                        {selected?.value === option.value && (
                          <CheckIcon className="tw-h-4 tw-w-4 tw-text-focus-100" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body,
        )}

      {name && <input type="hidden" name={name} value={value ?? ''} />}
    </div>
  )
}
